import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input, Select,
  message, Alert, Tag, Tabs, Switch, Popconfirm, Tooltip, Drawer, Divider, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, WhatsAppOutlined,
  SendOutlined, EyeOutlined, CopyOutlined,
  CheckCircleOutlined, SafetyCertificateOutlined, TagsOutlined, SearchOutlined,
} from '@ant-design/icons'
import { useMasterData, type WhatsAppTemplate } from '@/context/MasterDataContext'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { generateCode } from '@/utils/codeGenerator'

const BASE = '/master/whatsapp-templates'

// Placeholder tokens get their own color everywhere they're shown (the
// reference list and the live-typing preview) so they visually stand out
// from the surrounding message text.
const PLACEHOLDER_COLOR = '#0F9D6C'
const PLACEHOLDER_BG = '#E6F7EF'
const PLACEHOLDER_SPLIT_RE = /(\{\{[^}]+\}\})/g
const isPlaceholderToken = (s: string) => /^\{\{[^}]+\}\}$/.test(s)

const PlaceholderChip: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <span style={{
    color: PLACEHOLDER_COLOR, background: PLACEHOLDER_BG, fontWeight: 600,
    borderRadius: 4, padding: '1px 6px', fontFamily: 'monospace', fontSize: 11,
    ...style,
  }}>
    {children}
  </span>
)

// Live "as you type" preview: same text the user is editing, with every
// {{placeholder}} token colored so it's easy to spot at a glance. Unlike the
// chat-bubble Preview Drawer (which substitutes sample values), this shows
// the raw text so the user can check placeholder syntax while composing.
const HighlightedPreview: React.FC<{ text?: string }> = ({ text }) => {
  if (!text || !text.trim()) return null
  const parts = text.split(PLACEHOLDER_SPLIT_RE)
  return (
    <div style={{
      fontSize: 12, color: '#334155', background: '#F8FAFC', border: '1px dashed #E2E8F0',
      borderRadius: 6, padding: '8px 10px', whiteSpace: 'pre-wrap', marginTop: 6,
    }}>
      {parts.map((part, i) => isPlaceholderToken(part)
        ? <PlaceholderChip key={i}>{part}</PlaceholderChip>
        : <React.Fragment key={i}>{part}</React.Fragment>
      )}
    </div>
  )
}

// Business use-case categories — same taxonomy as Email Template Master, so
// managers pick a template the same way regardless of channel.
const CATEGORIES = [
  'Follow Up',
  'Proposal',
  'Invoice',
  'Payment Reminder',
  'Welcome',
  'General',
]

const CATEGORY_COLOR: Record<string, string> = {
  'Follow Up': 'blue',
  Proposal: 'geekblue',
  Invoice: 'purple',
  'Payment Reminder': 'volcano',
  Welcome: 'green',
  General: 'default',
}

// WhatsApp Business API's own classification — required once templates are
// submitted to a real BSP (Meta Cloud API / Gupshup / Interakt / …).
const WA_CATEGORIES = ['Marketing', 'Utility', 'Authentication']
const WA_CATEGORY_COLOR: Record<string, string> = {
  Marketing: 'magenta', Utility: 'blue', Authentication: 'orange',
}

const LANGUAGES = ['English', 'Tamil', 'Hindi', 'Telugu', 'Kannada', 'Malayalam']

const APPROVAL_STATUSES = ['Draft', 'Pending', 'Approved', 'Rejected']
const APPROVAL_COLOR: Record<string, string> = {
  Draft: 'default', Pending: 'gold', Approved: 'green', Rejected: 'red',
}

const BUTTON_TYPES = [
  { value: 'QUICK_REPLY', label: 'Quick Reply' },
  { value: 'URL', label: 'Website URL' },
  { value: 'PHONE_NUMBER', label: 'Call Phone Number' },
]

// Placeholders available to managers — filled in at send time
const PLACEHOLDERS = [
  '{{customerName}}', '{{companyName}}', '{{contactPerson}}',
  '{{proposalNumber}}', '{{proposalDate}}', '{{invoiceNumber}}', '{{amount}}', '{{dueDate}}',
  '{{senderName}}', '{{senderPhone}}',
]

// The body a new template starts with (manager edits freely)
const DEFAULT_BODY =
  'Hello {{customerName}} from {{companyName}},\n\n' +
  'Thank you for contacting Shakthi Power. A member of our team will reach out to you shortly.\n\n' +
  'Regards,\n{{senderName}}\nShakthi Power'

// Replace placeholders with readable sample values for the on-screen preview
const fillSampleValues = (text: string): string =>
  text
    .replace(/\{\{\s*customerName\s*\}\}/g, 'Mr. Ramesh Kumar')
    .replace(/\{\{\s*companyName\s*\}\}/g, 'ABC Textiles Ltd')
    .replace(/\{\{\s*contactPerson\s*\}\}/g, 'Mr. Ramesh Kumar')
    .replace(/\{\{\s*proposalNumber\s*\}\}/g, 'SP/PROP/2026/001')
    .replace(/\{\{\s*proposalDate\s*\}\}/g, '01-07-2026')
    .replace(/\{\{\s*invoiceNumber\s*\}\}/g, 'SP/INV/2026/045')
    .replace(/\{\{\s*amount\s*\}\}/g, '₹4,85,000')
    .replace(/\{\{\s*dueDate\s*\}\}/g, '10-07-2026')
    .replace(/\{\{\s*senderName\s*\}\}/g, 'Sathya Priya')
    .replace(/\{\{\s*senderPhone\s*\}\}/g, '+91 98765 43210')
    .replace(/\{\{[^}]+\}\}/g, '—')

// WhatsApp's own lightweight markdown (*bold*, _italic_, ~strike~) → HTML,
// for the chat-bubble preview.
const renderWaMarkdown = (text: string): string =>
  fillSampleValues(text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*([^*]+)\*/g, '<b>$1</b>')
    .replace(/_([^_]+)_/g, '<i>$1</i>')
    .replace(/~([^~]+)~/g, '<s>$1</s>')
    .replace(/\n/g, '<br/>')

const WhatsAppTemplateMaster: React.FC = () => {
  const { whatsappTemplates: templates, setWhatsappTemplates: setTemplates, deleteWhatsappTemplate, saveWhatsappTemplate, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [previewTemplate, setPreviewTemplate] = useState<WhatsAppTemplate | null>(null)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const headerVal = Form.useWatch('headerText', form)
  const bodyVal = Form.useWatch('bodyText', form)
  const footerVal = Form.useWatch('footerText', form)

  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('')

  const activeCount = templates.filter(t => t.status === 'active').length
  const approvedCount = templates.filter(t => t.approvalStatus === 'Approved').length

  const filteredTemplates = templates.filter(t => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      t.name.toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q) ||
      (t.category || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || t.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
      form.setFieldsValue({
        status: 'active',
        category: 'Follow Up',
        whatsappCategory: 'Utility',
        language: 'English',
        approvalStatus: 'Approved',
        isDefault: false,
        bodyText: DEFAULT_BODY,
        buttons: [],
      })
    } else if (mode === 'edit') {
      const rec = templates.find(t => t.id === editId)
      if (rec) form.setFieldsValue({ ...rec })
      else goList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, templates])

  const onFinish = async (values: any) => {
    const isEdit = mode === 'edit'
    let id = isEdit ? editId! : ''
    if (!isEdit) {
      try { id = await generateCode('whatsapp_templates') } catch { id = genId('WT') }
    }
    const payload = { ...values, id }

    setSubmitting(true)
    try {
      await saveWhatsappTemplate(payload, isEdit)
      // Only one default per category — unset the others in the same
      // category, a secondary best-effort local update persisted via the
      // generic write-through sync, not this record.
      if (values.isDefault) {
        setTemplates(prev => prev.map(t =>
          t.id !== payload.id && t.category === values.category ? { ...t, isDefault: false } : t
        ))
      }
      message.success(isEdit ? 'Template updated' : 'Template created')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const handleDuplicate = async (t: WhatsAppTemplate) => {
    const clone = { ...t, id: genId('WT'), name: `${t.name} (Copy)`, isDefault: false }
    try {
      await saveWhatsappTemplate(clone, false)
      message.success('Template duplicated')
    } catch { /* error already shown */ }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteWhatsappTemplate(id)
      message.success('Template deleted')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'name',
      render: (v: string, r: WhatsAppTemplate) => (
        <div>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#1677ff' }}>
            {v}
          </span>
          {r.isDefault && (
            <Tag color="gold" style={{ marginLeft: 8, fontSize: 10 }}>Default</Tag>
          )}
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{r.description}</div>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      width: 130,
      render: (c: string) => <Tag color={CATEGORY_COLOR[c] || 'default'}>{c}</Tag>,
    },
    {
      title: 'WA Category',
      dataIndex: 'whatsappCategory',
      width: 120,
      render: (c: string) => <Tag color={WA_CATEGORY_COLOR[c] || 'default'}>{c}</Tag>,
    },
    {
      title: 'Language',
      dataIndex: 'language',
      width: 100,
      render: (l: string) => <span style={{ fontSize: 12, color: '#475569' }}>{l}</span>,
    },
    {
      title: 'Approval',
      dataIndex: 'approvalStatus',
      width: 110,
      render: (s: string) => <Tag color={APPROVAL_COLOR[s] || 'default'}>{s}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 90,
      render: (s: string) => (
        <Tag color={s === 'active' ? 'green' : 'default'}>
          {s === 'active' ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: WhatsAppTemplate) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="Preview">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setPreviewTemplate(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Delete this template?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ─────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? '✏️ Edit WhatsApp Template' : '✨ New WhatsApp Template'}
        subtitle="Reusable WhatsApp message template — header, body, footer, buttons and placeholders"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this template?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Tabs
            items={[
              // ── Tab 1: Basic Info ────────────────────────────────
              {
                key: 'basic',
                label: '📋 Basic Info',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                      <Form.Item name="name" label="Template Name" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Proposal Follow-up" />
                      </Form.Item>
                      <Form.Item name="category" label="Category" initialValue="Follow Up" rules={[{ required: true }]}>
                        <Select options={CATEGORIES.map(c => ({ value: c, label: c }))} />
                      </Form.Item>
                    </div>
                    <Form.Item name="description" label="Short Description (shown in list)">
                      <Input placeholder="e.g. Gentle follow-up after sharing a proposal" />
                    </Form.Item>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
                      <Form.Item name="whatsappCategory" label="WhatsApp Category" initialValue="Utility" rules={[{ required: true }]}>
                        <Select options={WA_CATEGORIES.map(c => ({ value: c, label: c }))} />
                      </Form.Item>
                      <Form.Item name="language" label="Language" initialValue="English">
                        <Select options={LANGUAGES.map(l => ({ value: l, label: l }))} />
                      </Form.Item>
                      <Form.Item name="approvalStatus" label="Approval Status" initialValue="Approved">
                        <Select options={APPROVAL_STATUSES.map(s => ({ value: s, label: s }))} />
                      </Form.Item>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                      <Form.Item name="status" label="Status" initialValue="active">
                        <Select options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
                      </Form.Item>
                      <Form.Item name="isDefault" label="Default for this category" valuePropName="checked">
                        <Switch checkedChildren="Yes" unCheckedChildren="No" />
                      </Form.Item>
                    </div>
                  </div>
                ),
              },
              // ── Tab 2: Message Content ────────────────────────────
              {
                key: 'content',
                label: '💬 Message Content',
                children: (
                  <div style={{ paddingTop: 12 }}>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="WhatsApp supports *bold*, _italic_ and ~strikethrough~ — no other formatting."
                      description={
                        <span style={{ fontSize: 12 }}>
                          Available placeholders:{' '}
                          {PLACEHOLDERS.map(p => (
                            <PlaceholderChip key={p} style={{ marginRight: 6 }}>{p}</PlaceholderChip>
                          ))}
                        </span>
                      }
                    />
                    <Form.Item name="headerText" label="Header (optional, one line)" style={{ marginBottom: 0 }}>
                      <Input placeholder="e.g. Shakthi Power — {{companyName}}" maxLength={60} showCount />
                    </Form.Item>
                    <HighlightedPreview text={headerVal} />
                    <Form.Item name="bodyText" label="Body" rules={[{ required: true }]} style={{ marginBottom: 0, marginTop: 16 }}>
                      <Input.TextArea placeholder="Type the message here…" autoSize={{ minRows: 6, maxRows: 14 }} maxLength={1024} showCount />
                    </Form.Item>
                    <HighlightedPreview text={bodyVal} />
                    <Form.Item name="footerText" label="Footer (optional, small text)" style={{ marginBottom: 0, marginTop: 16 }}>
                      <Input placeholder="e.g. Shakthi Power — Green Energy Solutions" maxLength={60} showCount />
                    </Form.Item>
                    <HighlightedPreview text={footerVal} />

                    <Divider style={{ margin: '8px 0 16px' }} />
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Buttons (optional, up to 3)</div>
                    <Form.List name="buttons">
                      {(fields, { add, remove }) => (
                        <div>
                          {fields.map(({ key, name, ...rest }) => (
                            <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
                              <Form.Item {...rest} name={[name, 'type']} initialValue="QUICK_REPLY" style={{ width: 170, marginBottom: 0 }}>
                                <Select options={BUTTON_TYPES} />
                              </Form.Item>
                              <Form.Item {...rest} name={[name, 'label']} rules={[{ required: true, message: 'Label' }]} style={{ width: 180, marginBottom: 0 }}>
                                <Input placeholder="Button label" maxLength={25} />
                              </Form.Item>
                              <Form.Item {...rest} name={[name, 'value']} style={{ flex: 1, marginBottom: 0 }}>
                                <Input placeholder="URL or phone number (not used for Quick Reply)" />
                              </Form.Item>
                              <Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} />
                            </div>
                          ))}
                          {fields.length < 3 && (
                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ type: 'QUICK_REPLY', label: '', value: '' })} block>
                              Add Button
                            </Button>
                          )}
                        </div>
                      )}
                    </Form.List>
                  </div>
                ),
              },
            ]}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={submitting}>
              {mode === 'edit' ? 'Save Changes' : 'Create Template'}
            </Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={7} />

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <WhatsAppOutlined style={{ color: '#25D366' }} /> WhatsApp Template Master
          </h2>
          <p className="page-subtitle">
            Create and manage reusable WhatsApp message templates — follow-ups, proposal shares, payment
            reminders and more. Each template has a header, body, footer and optional buttons, with
            placeholders that are filled in when the message is sent.
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>
          New Template
        </Button>
      </div>

      {/* KPI */}
      <KpiCardRow
        cards={[
          { label: 'Total Templates', value: templates.length, icon: <WhatsAppOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Approved', value: approvedCount, icon: <SafetyCertificateOutlined style={{ fontSize: 24, color: '#25D366' }} />, iconBg: 'rgba(37,211,102,0.08)', borderColor: '#25D366' },
          { label: 'Categories', value: new Set(templates.map(t => t.category)).size, icon: <TagsOutlined style={{ fontSize: 24, color: '#F59E0B' }} />, iconBg: 'rgba(245,158,11,0.08)', borderColor: '#F59E0B' },
        ]}
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, borderRadius: 8 }}
        message="Use placeholders like {{customerName}}, {{companyName}} and {{senderName}} in the header and body — they are replaced with real values when the message is sent."
      />

      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by name, description or category..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredTemplates}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} templates` }}
        />
      </Card>

      {/* ── Preview Drawer ───────────────────────────────────────── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>💬 Preview — {previewTemplate?.name}</div>
            <div style={{ fontSize: 12, color: '#8A93A6' }}>{previewTemplate?.description}</div>
          </div>
        }
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        width={420}
        extra={
          <Space>
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined />} onClick={() => { if (previewTemplate) goEdit(previewTemplate.id); setPreviewTemplate(null) }} />
            </Tooltip>
            <Tooltip title="Duplicate">
              <Button type="text" icon={<CopyOutlined />} onClick={() => { handleDuplicate(previewTemplate!); setPreviewTemplate(null) }} />
            </Tooltip>
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this template?"
                description="This action cannot be undone."
                onConfirm={() => { handleDelete(previewTemplate!.id); setPreviewTemplate(null) }}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </Space>
        }
      >
        {previewTemplate && (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ marginBottom: 16 }}>
              <Tag color={CATEGORY_COLOR[previewTemplate.category] || 'default'}>{previewTemplate.category}</Tag>
              <Tag color={WA_CATEGORY_COLOR[previewTemplate.whatsappCategory] || 'default'}>{previewTemplate.whatsappCategory}</Tag>
              <Tag color={APPROVAL_COLOR[previewTemplate.approvalStatus] || 'default'}>{previewTemplate.approvalStatus}</Tag>
              {previewTemplate.isDefault && <Tag color="gold">Default</Tag>}
            </div>

            {/* WhatsApp-style chat bubble preview */}
            <div style={{ background: '#E5DDD5', borderRadius: 8, padding: 16 }}>
              <div style={{
                background: '#fff', borderRadius: '8px 8px 8px 0', padding: '10px 12px',
                maxWidth: '92%', boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
              }}>
                {previewTemplate.headerText && (
                  <div style={{ fontWeight: 700, marginBottom: 6 }}
                    dangerouslySetInnerHTML={{ __html: renderWaMarkdown(previewTemplate.headerText) }} />
                )}
                <div dangerouslySetInnerHTML={{ __html: renderWaMarkdown(previewTemplate.bodyText) }} />
                {previewTemplate.footerText && (
                  <div style={{ fontSize: 11, color: '#8696A0', marginTop: 8 }}>
                    {previewTemplate.footerText}
                  </div>
                )}
                {!!previewTemplate.buttons?.length && (
                  <div style={{ marginTop: 10, borderTop: '1px solid #f0f0f0', paddingTop: 6 }}>
                    {previewTemplate.buttons.map((b, i) => (
                      <div key={i} style={{
                        textAlign: 'center', color: '#00A5F4', fontSize: 13, fontWeight: 500,
                        padding: '8px 0', borderTop: i > 0 ? '1px solid #f0f0f0' : undefined,
                      }}>
                        {b.type === 'URL' ? '🔗 ' : b.type === 'PHONE_NUMBER' ? '📞 ' : ''}{b.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 12 }}>
              Placeholders shown with sample values. Real values are used when the message is sent.
            </div>
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default WhatsAppTemplateMaster
